import { memo } from "react";
import { useTranslation } from "react-i18next";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { PriceDisplay } from "@/components/ui/price-display";
import type { CustomerDetailFragment } from "@/gql/graphql";
import { useConfig } from "@/hooks/configuration";
import { getCustomerInitials, getCustomerPhoneDisplay } from "../../utils/customer-utils";

interface CustomerListItemProps {
  customer: CustomerDetailFragment;
  onClick: () => void;
  isLast?: boolean;
}

export const CustomerListItem = memo(
  ({ customer, onClick, isLast = false }: CustomerListItemProps) => {
    const { t } = useTranslation("customers");
    const { value: loyaltyEnabled = false } = useConfig("loyalty.enabled");

    return (
      <button
        type="button"
        className={`flex w-full items-center justify-between p-4 hover:bg-muted cursor-pointer ${
          !isLast ? "border-b border-border" : ""
        }`}
        onClick={onClick}
      >
        <div className="flex items-center space-x-4">
          <Avatar>
            <AvatarImage src={customer.avatar || undefined} />
            <AvatarFallback>{getCustomerInitials(customer)}</AvatarFallback>
          </Avatar>
          <div className="text-left">
            <div className="font-medium text-foreground">{`${customer.firstName} ${customer.lastName}`}</div>
            <div className="text-sm text-muted-foreground">{customer.email}</div>
            <div className="text-sm text-muted-foreground">{getCustomerPhoneDisplay(customer)}</div>
          </div>
        </div>
        <div className="text-right">
          {loyaltyEnabled && (
            <div className="flex items-center gap-1 justify-end mb-1">
              <Badge
                className="capitalize"
                style={{
                  backgroundColor: customer.currentTier?.color || "#64748b",
                  color: "white",
                }}
              >
                {customer.currentTier?.name || t("list.item.noTier")}
              </Badge>
            </div>
          )}
          <div className="text-sm text-muted-foreground">
            {customer.totalVisits}{" "}
            {customer.totalVisits === 1 ? t("list.item.visit") : t("list.item.visits")}
          </div>
          <div className="text-sm font-medium text-foreground">
            <PriceDisplay amount={customer.totalSpent} />
          </div>
        </div>
      </button>
    );
  }
);
